import { Link } from "react-router-dom";
import Seo from "@/lib/Seo";
import { useFetch } from "@/lib/api";
import { FadeUp } from "@/lib/motion";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function Services() {
  const { data: services, loading, error } = useFetch("/services");

  return (
    <div className="pt-[72px]">
      <Seo
        title="Wedding Services — Perfume Bar, Concierge, Decor & More | Marble & Veil"
        description="Staffed guest experiences and coordinated decor for Southern California weddings: perfume bar, wedding concierge, coat check, signage, tablescapes, florals, and dance floor styling."
        path="/services"
      />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <Breadcrumbs trail={[["Home", "/"], ["Services"]]} />
        <FadeUp>
          <h1 className="mt-6 font-display text-5xl leading-[1.05] text-ink sm:text-6xl">Services</h1>
          <p className="mt-5 max-w-xl leading-relaxed text-smoke">
            Staffed guest experiences and coordinated styling, arranged around your venue and scoped in
            your proposal. An inquiry never guarantees stock, staffing, or a date — availability is
            confirmed personally.
          </p>
        </FadeUp>

        {loading && <p className="mt-16 text-smoke" data-testid="services-loading">Gathering services…</p>}
        {error && <p className="mt-16 text-red-900" data-testid="services-error" role="alert">Services could not be loaded just now. Please refresh in a moment.</p>}

        <div className="mt-16 space-y-20 lg:space-y-28">
          {(services || []).map((s, i) => (
            <FadeUp key={s.slug}>
              <div className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-16 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`service-row-${s.slug}`}>
                <Link to={`/services/${s.slug}`} className="block overflow-hidden bg-parch">
                  <img src={s.image} alt={`${s.name} — design inspiration`} className="aspect-[4/3] w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.04]" width="900" height="675" loading="lazy" />
                </Link>
                <div>
                  {s.status === "coming_soon" && (
                    <span className="inline-block border border-olive/50 px-3 py-1 text-[10px] uppercase tracking-widest2 text-olive" data-testid={`services-coming-soon-${s.slug}`}>
                      Coming soon
                    </span>
                  )}
                  <h2 className="mt-3 font-display text-3xl text-ink sm:text-4xl">
                    <Link to={`/services/${s.slug}`} className="transition-colors hover:text-olive">{s.name}</Link>
                  </h2>
                  <p className="mt-2 font-display text-xl italic text-olive">{s.tagline}</p>
                  <p className="mt-4 max-w-lg leading-relaxed text-smoke">{s.short}</p>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`services-cta-${s.slug}`}
                    className="mt-6 inline-block border border-ink/20 px-7 py-3 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-olive hover:bg-olive hover:text-ivory"
                  >
                    {s.status === "coming_soon" ? "Ask About Future Availability" : "Request Availability"}
                  </Link>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}
